import React, {useEffect, useState} from 'react';
import Layout from "../components/Layout";
import axios from "axios";
import {
	getRunningQueriesThunk,
	getSocialLoginLinks,
	useGetInviteCodeQuery,
	useGetMeCheckQuery,
	useGetSocialLoginLinksQuery,
	usePostSignupMutation
} from "../rtk-api/auth";
import {everythingAfter, getSingleErrors} from "../helpers/utils";
import {useGeneralStore} from "../stores/general";
import SocialLogins from "./inc/socialLogins";
import { getCookie, deleteCookie } from 'cookies-next';
import {wrapper} from "../store";
import Link from "next/link";
import {NextSeo} from "next-seo";

export const getServerSideProps = wrapper.getServerSideProps(store => async ({req, query}) => {
	store.dispatch(getSocialLoginLinks.initiate());
	await Promise.all(store.dispatch(getRunningQueriesThunk()));
	return {
		props: {
			pricingInfo: everythingAfter('?', req.url),
			inviteCode: query.invite_code ?? null,
			cycle: query.cycle ?? 'annual',
			tier: query.tier ?? 'free',
			quantity: query.quantity ?? 1,
			allowNameRegistration: Boolean(parseInt(query.allow_name_registration)) ?? false
		},
	};
});

function SignUp({ cycle, tier, quantity, pricingInfo, inviteCode, allowNameRegistration }) {
	const {data:getSocialLinks} = useGetSocialLoginLinksQuery()
	const {data:getInviteCode} = useGetInviteCodeQuery({inviteCode});
	const setShowGlobalLoading = useGeneralStore((state) => state.setShowGlobalLoading)
	const [pageName, setPageName] = useState(getInviteCode?.pre_register_username ?? undefined);
	const [email, setEmail] = useState(getInviteCode?.email ?? undefined);
	const [password, setPassword] = useState();
	const [passwordConf, setPasswordConf] = useState();
	const [postSignup, { error }] = usePostSignupMutation()
	const {data:meInfo} = useGetMeCheckQuery()
	const [errorList, setErrorList] = useState(false);
	const [submitText, setSubmitText] = useState('Sign Up');
	const [disableSubmit, setDisableSubmit] = useState(false);
	
	useEffect(() => {
		if (error) {
			setErrorList(error.data.errors)
			setShowGlobalLoading(false)
		}
	}, [error]);
	
	useEffect(() => {
		if (getInviteCode) {
			setPageName(getInviteCode?.pre_register_username)
			setEmail(getInviteCode?.email)
		}
	}, [getInviteCode?.pre_register_username, getInviteCode?.email]);
	
	const clearErrors = () => {
		setErrorList(false);
	}
	
	const handleSignup = async (e) => {
		e.preventDefault();
		clearErrors()
		setSubmitText('Please Wait...')
		setDisableSubmit(true)
		deleteCookie('XSRF-TOKEN')
		
		axios.defaults.withCredentials = true;
		axios.get(process.env.NEXT_PUBLIC_API_URL + '/sanctum/csrf-cookie').then(() => {
			postSignup({
				pageName,
				email,
				password,
				passwordConf,
				allowNameRegistration,
				inviteCode,
				timezone:Intl.DateTimeFormat()?.resolvedOptions()?.timeZone,
				affiliateCode: getCookie('ref_id') ?? null,
				cycle,
				quantity,
				tier,
				fbp: getCookie('_fbp') ?? null,
				fbc: getCookie('_fbc') ?? null,
				gaCookie: getCookie('_ga') ?? null
			})
				.unwrap()
				.then((response) => {
					deleteCookie('ref_id', { path: '/', domain: process.env.NEXT_PUBLIC_DOMAIN });
					setShowGlobalLoading(true)
					if (!response.checkout) {
						window.location.replace(process.env.NEXT_PUBLIC_APP_URL);
					} else {
						window.location.replace(process.env.NEXT_PUBLIC_ORIGINAL_URL + '/checkout?' + response.checkout);
					}
				})
				.catch(() => {
					setDisableSubmit(false)
					setSubmitText('Login')
				})
		})
	}
	
	function showError(key) {
		return getSingleErrors(key, errorList)
	}
	
	const pageNameValue = () => {
		return allowNameRegistration === true ? getInviteCode?.pre_register_username : undefined
	}
	
	const isPageNameDisabled = () => {
		return allowNameRegistration === true && getInviteCode?.pre_register_username != null
	}
	
	const emailValue = () => {
		return getInviteCode?.email ?? undefined
	}
	
	if (meInfo) {
		setShowGlobalLoading(true)
		if (meInfo.is_logged_in) {
			window.location.replace(process.env.NEXT_PUBLIC_APP_URL);
		} else {
			setShowGlobalLoading(false)
			return (
				<Layout title="Sign Up">
					<NextSeo
						title="PageName - Create an Account"
						description="Login to PageName"
						openGraph={{
							type: 'website',
							url: process.env.NEXT_PUBLIC_AUTH_URL,
							title: 'PageName - Create an Account',
							description: 'Create an Account for PageName',
							images: [
								{
									url: process.env.NEXT_PUBLIC_AUTH_URL + '/images/og-image.png',
									width: 1200,
									height: 641,
									alt: 'Create an Account for PageName',
									type: 'image/png',
								}
							],
							siteName: 'PageName',
						}}
						twitter={{
							site: '@pagenamer',
							cardType: 'summary_large_image',
						}}
					/>
					<section className="main-content signin section illustration-section-01 has-top-divider px-10">
						<div className="container mx-auto">
							<div className="signin-inner section-inner ">
								<div className="section-header text-center mb-10">
									<h1 className="heading text-4xl -mx-2">Create a PageName Account</h1>
									{getInviteCode?.active &&
										<p className="alert alert-success my-2">
											You are being invited to
											{ allowNameRegistration ? ' take over  ' : ' manage ' }
											<b>{getInviteCode?.inviters_page_name}</b>&apos;s page.
										</p>
									}
								</div>
								
								<div className="tiles-wrap">
									<div className="col-10">
										<div className="tiles-item-inner">
											<div className="row">
												<SocialLogins
													socialLinks={getSocialLinks.data}
													queryString={pricingInfo}
													text='Sign up'
												/>
											</div>
											
											<div className="separator my-6">OR</div>
											
											{Object.keys(errorList).length !== 0 &&
												<div className="alert alert-danger js_alert" data-cy="alert-danger" role="alert">
													<div>{showError('page_name')}</div>
													<div>{showError('email')}</div>
													<div>{showError('password')}</div>
													<div>{showError('password_conf')}</div>
												</div>
											}
											
											<form onSubmit={e => handleSignup(e)}>
												<fieldset className="border-b pb-10 mb-10">
													<div className="mb-3">
														<div className="text-lg inline-block">
															{process.env.NEXT_PUBLIC_ORIGINAL_URL}/
														</div>
														<label className="sr-only" htmlFor="page_name">PageName</label>
														<input
															type="text"
															data-cy="page_name"
															name="page_name"
															className="form-input"
															style={{
																width: "auto",
																display: "inline-flex",
																flex: "0 0 50%"
															}}
															placeholder="mypagename"
															autoComplete="page_name"
															disabled={isPageNameDisabled()}
															defaultValue={pageNameValue()}
															required
															onChange={e => {
																clearErrors()
																setPageName(e.target.value)
															}}
														/>
													</div>
													<div className="mb-3">
														<label className="sr-only" htmlFor="email">Username / Email Address</label>
														<input
															id="email"
															data-cy="name"
															name="email"
															className="form-input"
															type="text"
															placeholder="Email Address or pagename"
															defaultValue={emailValue()}
															onChange={e => {
																clearErrors()
																setEmail(e.target.value)
															}}
															required/>
													</div>
													<div className="mb-3">
														<label className="sr-only" htmlFor="password">Password</label>
														<input
															id="password"
															data-cy="password"
															name="password"
															className="form-input"
															type="password"
															placeholder="Password"
															onChange={e => {
																clearErrors()
																setPassword(e.target.value)
															}}
															required
														/>
													</div>
													<div className="mb-3">
														<label className="sr-only" htmlFor="password">Password (Again)</label>
														<input
															id="password_again"
															data-cy="password"
															name="password"
															className="form-input"
															type="password"
															placeholder="Password (Again)"
															onChange={e => {
																clearErrors()
																setPasswordConf(e.target.value)
															}}
															required
														/>
													</div>
													<div className="my-6 leading-relaxed text-sm">
														By clicking Sign Up, you agree to our <a href={process.env.NEXT_PUBLIC_ORIGINAL_URL + '/legal/terms'}>Terms</a>, <a href={process.env.NEXT_PUBLIC_ORIGINAL_URL + '/legal/privacy'}>Privacy  Policy</a> and <a href={process.env.NEXT_PUBLIC_ORIGINAL_URL + '/legal/cookies'}>Cookies Policy</a>. You may receive offers, news and updates from us.
													</div>
													<div className="mb-6">
														<button
															type="submit"
															data-cy="signup"
															disabled={disableSubmit}
															className="btn-primary w-full py-3 uppercase"
														>{submitText}</button>
													</div>
												</fieldset>
											</form>
											<div className="mb-10">
												<div className="text-center">
													Already have an account? <Link className="func-link" href="/login">Log In</Link>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</section>
				</Layout>
			);
		}
	}
}

export default SignUp;

